// Layer 4 (UI) — the event-log crew and time-of-day comparison (rebuilt to the
// locked comp `downtime.html` A: each table carries its own count bars).
// Every figure comes from ShiftCorrelation.transform.ts; nothing is typed here.

import { Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { KpiCard } from '@/versions/v1/modules/_viz';
import { formatCurrency } from '@/versions/v1/modules/capacity/Scene.transform';
import { Commentable } from '@/shared/comments/Commentable';
import { ActorChip } from '../advisory/PipelineStrip';
import { KpiStrip } from '../maintenance/KpiStrip';
import { downtimeEvents } from '../maintenance/DowntimeEvents.transform';
import { formatHours } from '../maintenance/Downtime.transform';
import { CARD_TABLE_DENSE, DowntimeCard, HOT_ROW, RankBar, SYN_CHIP } from './DowntimeCard';
import { shiftCorrelationRead } from './ShiftCorrelation.transform';

function CountCell({ count, max, top }: { count: number; max: number; top: boolean }) {
  return (
    <span className="flex items-center gap-2">
      <RankBar className="flex-1" ratio={count / max} tone={top ? 'top' : 'ink'} />
      <span className="w-6 shrink-0 text-right font-semibold text-ink-primary">{count}</span>
    </span>
  );
}

export function ShiftCorrelationTab() {
  const read = shiftCorrelationRead(downtimeEvents());
  const { busiestCrew, peakWindow, worstVsField, hypotheses } = read;
  const crewMax = Math.max(1, ...read.crews.map((row) => row.eventCount));
  const windowMax = Math.max(1, ...read.windows.map((row) => row.eventCount));

  return (
    <Commentable name="ShiftCorrelation">
      <div className="space-y-3">
        <KpiStrip>
          <KpiCard label="Most events" value={`${busiestCrew.eventCount} events`} sub={`${busiestCrew.panel} · ${Math.round(busiestCrew.sharePct)}% · ${formatHours(busiestCrew.hours)} · ${read.averagePerCrew.toFixed(1)} avg/crew`} tone="bad" className="px-3.5 py-3" />
          <KpiCard label="Peak 6-hour window" value={peakWindow.label} sub={`${Math.round(peakWindow.sharePct)}% of events`} tone="warn" className="px-3.5 py-3" />
          <KpiCard label="Panel vs field" value={`${Math.round(worstVsField.crew.sharePct)}% vs ${Math.round(worstVsField.othersMeanSharePct)}% avg`} sub={worstVsField.crew.panel} tone="bad" className="px-3.5 py-3" />
          <KpiCard label="Grounded hypotheses" value={String(hypotheses.length)} sub={`${read.totalEvents} unplanned events`} tone="info" className="px-3.5 py-3" />
        </KpiStrip>

        <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-start">
          <DowntimeCard title="Events by shift panel" meta="YTD 2026" actions={SYN_CHIP} className="xl:col-span-7">
            <div className={CARD_TABLE_DENSE}>
              <Table wrap={false} className="tabular-nums">
                <THead><TR><TH>Panel</TH><TH className="w-[30%]">Events</TH><TH className="text-right">Hours</TH><TH className="text-right">Process</TH><TH className="text-right">Equipment</TH><TH className="text-right">Value lost</TH></TR></THead>
                <TBody>
                  {read.crews.map((row) => {
                    const top = row.crewId === busiestCrew.crewId;
                    return (
                      <TR key={row.crewId} className={top ? HOT_ROW : undefined}>
                        <TD className="whitespace-nowrap font-medium !text-ink-primary">
                          {row.panel}
                          {top ? (
                            <span className="ml-2 inline-flex rounded-full bg-[color-mix(in_srgb,var(--state-blocked)_10%,transparent)] px-2 py-px text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--state-blocked)]">
                              Highest
                            </span>
                          ) : null}
                        </TD>
                        <TD><CountCell count={row.eventCount} max={crewMax} top={top} /></TD>
                        <TD className="whitespace-nowrap text-right">{formatHours(row.hours)}</TD>
                        <TD className="text-right">{row.processCount}</TD>
                        <TD className="text-right">{row.equipmentCount}</TD>
                        <TD className="whitespace-nowrap text-right font-semibold !text-ink-primary">{formatCurrency(row.dollars)}</TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          </DowntimeCard>

          <DowntimeCard title="Events by time of day" meta="All panels · YTD 2026" actions={SYN_CHIP} className="xl:col-span-5">
            <div className={CARD_TABLE_DENSE}>
              <Table wrap={false} className="tabular-nums">
                <THead><TR><TH>Window</TH><TH className="w-[44%]">Events</TH><TH className="text-right">%</TH></TR></THead>
                <TBody>
                  {read.windows.map((row) => {
                    const top = row.startHour === peakWindow.startHour;
                    return (
                      <TR key={row.startHour} className={top ? HOT_ROW : undefined}>
                        <TD className="whitespace-nowrap font-medium !text-ink-primary">{row.label}</TD>
                        <TD><CountCell count={row.eventCount} max={windowMax} top={top} /></TD>
                        <TD className="text-right">{Math.round(row.sharePct)}%</TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          </DowntimeCard>
        </div>

        <DowntimeCard aria-label="AI hypotheses" className="py-1.5">
          <div className="divide-y divide-divider">
            {hypotheses.map((row) => (
              <div key={row.label} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 text-sm">
                <span className="flex items-baseline gap-2 text-ink-primary">
                  <ActorChip actor="Engine">AI hypothesis</ActorChip>
                  {row.label}
                </span>
                <span className="text-xs tabular-nums text-muted">{row.evidence}</span>
              </div>
            ))}
          </div>
        </DowntimeCard>
      </div>
    </Commentable>
  );
}
